import { useCallback, useEffect, useLayoutEffect, useRef, useState, type Ref } from 'react';
import { getOwnerOverview } from '../api/dashboard';
import { getReport } from '../api/reports';
import { useAuth } from '../auth/AuthProvider';
import { canViewExpenses, getFinancialStoreOptions } from '../auth/permissions';
import { OwnerDashboardDetail } from '../components/OwnerDashboardDetail';
import { scrollParentOf } from '../components/pos/scrollParent';
import { ReportBusiness } from '../components/reports/ReportBusiness';
import { ReportCosts } from '../components/reports/ReportCosts';
import { ReportInventory } from '../components/reports/ReportInventory';
import { ReportMoney } from '../components/reports/ReportMoney';
import { ReportOperations } from '../components/reports/ReportOperations';
import { ReportProducts } from '../components/reports/ReportProducts';
import { ReportWhen } from '../components/reports/ReportWhen';
import { DETAIL_ID, ReportsLanding } from '../components/reports/ReportsLanding';
import { ReportsContext, ReportsHeader, ReportsPending } from '../components/reports/ReportsParts';
import { ReportsPeriod } from '../components/reports/ReportsPeriod';
import { AccessDeniedPage } from './AccessDeniedPage';
import { currentReportsScreen, leaveReport, openReportFromLanding, type ReportsScreen } from '../utils/reportsRoute';
import type { CostsReport, InventoryReport, OperationsReport, ProductsReport, WhenReport } from '../types/reports';
import { costsReportView } from '../utils/reports/costsView';
import { inventoryReportView } from '../utils/reports/inventoryView';
import { operationsReportView } from '../utils/reports/operationsView';
import { productsReportView } from '../utils/reports/productsView';
import { reportMetaContext } from '../utils/reports/shared';
import { whenReportView } from '../utils/reports/whenView';
import {
  businessReportView,
  defaultPeriod,
  isReportKind,
  loadFromError,
  loadFromReport,
  loadFromResponse,
  moneyReportView,
  referenceTimeZone,
  reportContext,
  reportLead,
  reportTitle,
  storeToday,
  type AppliedPeriod,
  type ReportId,
  type ReportLoad,
} from '../utils/reportsView';
import { useRequestGuard } from '../utils/useRequestGuard';

const LOADING: ReportLoad = { status: 'loading' };

/**
 * Reportes — Rediseño · Bloque F (`docs/UI_REDESIGN_BLOCK_F.md`) → P-084 (`docs/PHASE_P084_REPORTES_INTELIGENTES.md`).
 *
 * Ocho pantallas en la sección de siempre (`#owner-dashboard`, para no romper ningún enlace guardado):
 *
 *   #owner-dashboard             «Reportes»: las siete preguntas de P-030 y «Ver detalle»
 *   #owner-dashboard/negocio     R1 · ¿Cómo va mi negocio?          ─┐ GET /api/dashboard/owner-overview
 *   #owner-dashboard/dinero      R2 · ¿Dónde se va mi dinero?       ─┘
 *   #owner-dashboard/productos   R3 · ¿Qué productos me convienen?  ─┐
 *   #owner-dashboard/horarios    R4 · ¿Cuándo vendo más?             │
 *   #owner-dashboard/costos      R5 · ¿Cómo van mis costos?          ├ GET /api/dashboard/reports/:report (P-084)
 *   #owner-dashboard/inventario  R6 · ¿Cómo está mi inventario?      │
 *   #owner-dashboard/operacion   R7 · ¿Cómo va la operación?        ─┘
 *
 * Cada reporte pide UNA cosa con el rango del periodo; el servidor decide la tienda (N5) y toda cifra. El periodo es
 * de la página: elegido en un reporte, sigue en el otro. «Hoy» es el de la tienda de referencia del perfil (K8).
 * Lo que se ve es SIEMPRE la respuesta a la petición del reporte y el periodo de ahora: cada respuesta lleva la llave
 * (reporte y fechas) para la que se pidió, y una que no es la de ahora se ve como «cargando», nunca como cifras de
 * antes. Y solo cuenta la respuesta de la ÚLTIMA petición (`useRequestGuard`).
 */
export function OwnerDashboardPage({ onNavigate }: { onNavigate: (destination: string) => void }) {
  const { user, stores } = useAuth();
  const role = user?.role;
  const financial = getFinancialStoreOptions(role, stores);
  const timeZone = referenceTimeZone(role, stores);
  const screen = useReportsScreen();
  const [period, setPeriod] = useState<AppliedPeriod>(() => defaultPeriod(new Date(), timeZone));
  const [result, setResult] = useState<{ key: string; load: ReportLoad } | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // «Comparar con el periodo anterior» (P-030): solo de pantalla; no cambia lo que se pide.
  const [compare, setCompare] = useState(true);
  const request = useRequestGuard();
  const rootRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const shownScreen = useRef<ReportsScreen | null>(null);

  const key = screen === 'landing' ? null : `${screen}|${period.from}|${period.to}`;

  const fetchReport = useCallback((forKey: string, id: ReportId, range: AppliedPeriod) => {
    const isCurrent = request();
    setResult({ key: forKey, load: LOADING });
    if (isReportKind(id)) {
      getReport(id, { dateFrom: range.from, dateTo: range.to })
        .then((report) => { if (isCurrent()) setResult({ key: forKey, load: loadFromReport(id, report) }); })
        .catch((error: unknown) => { if (isCurrent()) setResult({ key: forKey, load: loadFromError(error) }); });
      return;
    }
    getOwnerOverview({ dateFrom: range.from, dateTo: range.to })
      .then((overview) => { if (isCurrent()) setResult({ key: forKey, load: loadFromResponse(overview) }); })
      .catch((error: unknown) => { if (isCurrent()) setResult({ key: forKey, load: loadFromError(error) }); });
  }, [request]);

  // Un reporte pide sus cifras al abrirse y cada vez que se elige un periodo (también el mismo: es la
  // forma de volver a pedirlo). La pantalla de preguntas no pide nada, y si al volver a ella quedaba una
  // petición en el aire, su respuesta ya no cuenta.
  useEffect(() => {
    if (financial.hasNoFinancialAccess) return;
    if (!key || screen === 'landing') {
      // Lo de antes ya no cuenta: al reabrir un reporte, su primer pintado es «cargando», no lo de antes.
      request();
      setResult(null);
      return;
    }
    fetchReport(key, screen, period);
  }, [key, screen, period, fetchReport, request, financial.hasNoFinancialAccess]);

  // Al cambiar de pantalla dentro de Reportes, la vista sube y el foco va al título: el shell solo lo
  // hace cuando cambia de sección, y aquí la sección es la misma. Al abrir la página no se mueve nada.
  useLayoutEffect(() => {
    if (shownScreen.current !== null && shownScreen.current !== screen) {
      const scroller = rootRef.current ? scrollParentOf(rootRef.current) : null;
      if (scroller) scroller.scrollTop = 0;
      titleRef.current?.focus();
    }
    shownScreen.current = screen;
  }, [screen]);

  if (financial.hasNoFinancialAccess) {
    return <AccessDeniedPage
      message="Tu usuario no tiene acceso a la información financiera de estas tiendas. Si crees que deberías verla, habla con el dueño del negocio."
      onReturn={() => onNavigate('dashboard')}
      title="No tienes permiso para ver los Reportes del negocio"
    />;
  }

  if (screen === 'landing') {
    return (
      <>
        <div className="n3-reports" ref={rootRef}>
          <ReportsLanding
            detailOpen={detailOpen}
            onOpen={openReportFromLanding}
            onToggleDetail={() => setDetailOpen((open) => !open)}
            titleRef={titleRef}
          />
        </div>
        {/* La vista analítica anterior, tal cual y con su piel de siempre: va FUERA de la raíz del rediseño. */}
        {detailOpen && <div id={DETAIL_ID}><OwnerDashboardDetail onNavigate={onNavigate} /></div>}
      </>
    );
  }

  const load = result && result.key === key ? result.load : LOADING;
  // Elegir un periodo —también el mismo— retira la respuesta en el MISMO pintado: la nueva petición sale
  // en un efecto, después de pintar, y sin esto se vería un cuadro con las cifras anteriores.
  const apply = (next: AppliedPeriod) => { setResult(null); setPeriod(next); };
  return (
    <div className="n3-reports" ref={rootRef}>
      <ReportScreen
        canOpenExpenses={canViewExpenses(role)}
        id={screen}
        load={load}
        compare={compare}
        onApply={apply}
        onToggleCompare={() => setCompare((value) => !value)}
        onNavigate={onNavigate}
        onRetry={() => fetchReport(key!, screen, period)}
        period={period}
        timeZone={timeZone}
        titleRef={titleRef}
      />
    </div>
  );
}

function ReportScreen({ id, load, period, compare, onApply, onToggleCompare, onRetry, onNavigate, canOpenExpenses, timeZone, titleRef }: {
  id: ReportId;
  compare: boolean;
  onToggleCompare: () => void;
  load: ReportLoad;
  period: AppliedPeriod;
  onApply: (period: AppliedPeriod) => void;
  onRetry: () => void;
  onNavigate: (destination: string) => void;
  canOpenExpenses: boolean;
  timeZone: string;
  titleRef: Ref<HTMLHeadingElement>;
}) {
  const today = storeToday(new Date(), timeZone);
  return (
    <section aria-busy={load.status === 'loading' || undefined} aria-labelledby="reports-title" className="n3-reports__screen">
      <ReportsHeader lead={reportLead(id)} onBack={leaveReport} title={reportTitle(id)} titleRef={titleRef} />
      {/* «Comparar» es un filtro de R1 en P-030; en los demás la comparación es parte de la respuesta (P-032, I3). */}
      <ReportsPeriod applied={period} compare={id === 'negocio' ? { on: compare, onToggle: onToggleCompare } : undefined} key={id} onApply={onApply} today={today} />
      {load.status === 'ready' && (
        <>
          <ReportsContext context={reportContext(load.overview, today)} />
          {id === 'negocio'
            ? <ReportBusiness onNavigate={onNavigate} view={businessReportView(load.overview, today, { compare })} />
            : <ReportMoney canOpenExpenses={canOpenExpenses} onNavigate={onNavigate} view={moneyReportView(load.overview, today)} />}
        </>
      )}
      {load.status === 'ready-report' && (
        <>
          <ReportsContext context={reportMetaContext(load.report.meta, today)} />
          {load.kind === 'horarios' && <ReportWhen onNavigate={onNavigate} view={whenReportView(load.report as WhenReport)} />}
          {load.kind === 'inventario' && <ReportInventory onNavigate={onNavigate} view={inventoryReportView(load.report as InventoryReport)} />}
          {load.kind === 'operacion' && <ReportOperations onNavigate={onNavigate} view={operationsReportView(load.report as OperationsReport)} />}
          {load.kind === 'costos' && <ReportCosts onNavigate={onNavigate} view={costsReportView(load.report as CostsReport)} />}
          {load.kind === 'productos' && <ReportProducts onNavigate={onNavigate} view={productsReportView(load.report as ProductsReport)} />}
        </>
      )}
      {load.status !== 'ready' && load.status !== 'ready-report' && <ReportsPending load={load} onRetry={onRetry} />}
    </section>
  );
}

/**
 * La pantalla que pide la URL. El router no vuelve a dibujar la página cuando el hash cambia dentro de la
 * misma sección, así que la página escucha el cambio ella misma.
 */
function useReportsScreen(): ReportsScreen {
  const [screen, setScreen] = useState<ReportsScreen>(currentReportsScreen);
  useEffect(() => {
    const onChange = () => setScreen(currentReportsScreen());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return screen;
}
